import { SUCCESS_MESSAGES } from '../../constant/messages';
import { logout } from '../../store/slices/authSlice';
import { handleApiError } from '../core/apiError';
import store from '../../store/store';
import axiosInstance from '../core/axiosInstance';

/**
 * Sends a request to the server to authenticate the user and log them in.
 * 
 * This function accepts the user's username and password, and sends a POST request 
 * to the login endpoint. The server response (such as the user token) will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} username - The user's email or username.
 * @param {string} password - The user's password.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data (e.g., user data or token).
 * @throws {Error} - Throws an error if the login request fails.
 */
export const loginRequest = async (credentials) => {
    try {
        const response = await axiosInstance.post('/auth/login', credentials);
        return response?.data; // Server response, typically contains user data or token
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Sends a request to the server to logout the user and end the current session.
 * 
 * This function removes the token from local session
 * so that user can logout from the application
 * and user is re-directed to the login page
 */
export const handleLogout = (navigate) => {
    store.dispatch(logout());  // Remove the token from localStorage
    localStorage.setItem('logoutSuccess', SUCCESS_MESSAGES.USER_LOGGED_OUT_SUCCESSFULLY);    // Add data to local storage
    navigate('/login');     // Redirect to the login page
};

/**
 * Sends a request to the server to register a new user.
 * 
 * This function accepts the user's details (username, password, and role), and sends a POST request
 * to the register endpoint to create a new user. The server response will contain the registration result.
 * 
 * @function
 * @param {string} username - The user's email or username.
 * @param {string} password - The user's password.
 * @param {string} role - The role of the user (e.g., 'admin', 'user').
 * @returns {Promise<Object>} - A promise that resolves to the server's response data (e.g., registration success).
 * @throws {Error} - Throws an error if the registration request fails.
 * 
 */
export const registerUser = async (userData) => {
    try {
        const response = await axiosInstance.post('/auth/register', userData);
        return response.data; // Server response, typically contains registration success or user data
    } catch (error) {
        handleApiError(error);
    }
};
